import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Routes, Route } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeUp = ({ children, delay = 0, style = {} }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
};

const SHARED_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  ::selection { background: rgba(255,255,255,0.12); }
  ::-webkit-scrollbar { width: 3px; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

  .mino-aurora {
    position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden;
  }
  .mino-aurora::before {
    content: '';
    position: absolute;
    width: 140%; height: 55%;
    top: -18%; left: -20%;
    background: conic-gradient(from 200deg at 50% 50%,
      transparent 0deg,
      rgba(55,114,255,0.09) 40deg,
      rgba(22,199,132,0.07) 80deg,
      rgba(124,58,237,0.08) 120deg,
      transparent 160deg
    );
    filter: blur(60px);
    animation: auroraShift 20s ease-in-out infinite alternate;
    transform-origin: center top;
  }
  .mino-aurora::after {
    content: '';
    position: absolute;
    width: 80%; height: 40%;
    bottom: 5%; right: -10%;
    background: conic-gradient(from 30deg at 50% 50%,
      transparent 0deg,
      rgba(22,199,132,0.06) 50deg,
      rgba(55,114,255,0.05) 100deg,
      transparent 150deg
    );
    filter: blur(70px);
    animation: auroraShift2 26s ease-in-out infinite alternate;
    transform-origin: center bottom;
  }
  .mino-beam {
    position: fixed; pointer-events: none; z-index: 0;
    width: 1px; top: 0; bottom: 0;
    background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.06) 60%, transparent 100%);
    animation: beamDrift var(--dur, 28s) ease-in-out infinite alternate;
  }
  .mino-dot-grid {
    position: fixed; inset: 0; pointer-events: none; z-index: 0;
    background-image: radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px);
    background-size: 40px 40px;
    mask-image: radial-gradient(ellipse 85% 85% at 50% 40%, black 40%, transparent 100%);
  }

  @keyframes auroraShift {
    0%   { transform: rotate(-4deg) scaleX(1);    opacity: 1; }
    50%  { transform: rotate(2deg)  scaleX(1.08); opacity: 0.85; }
    100% { transform: rotate(-6deg) scaleX(0.96); opacity: 1; }
  }
  @keyframes auroraShift2 {
    0%   { transform: rotate(6deg)  scaleX(1);    opacity: 0.8; }
    50%  { transform: rotate(-3deg) scaleX(1.1);  opacity: 1; }
    100% { transform: rotate(8deg)  scaleX(0.92); opacity: 0.75; }
  }
  @keyframes beamDrift {
    0%   { left: var(--from); opacity: 0.5; }
    100% { left: var(--to);   opacity: 1; }
  }
  @keyframes fadeSlideIn {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .mino-nav-link { font-family: 'Outfit', sans-serif; font-size: 13px; color: rgba(255,255,255,0.32); cursor: pointer; padding: 5px 12px; border-radius: 6px; transition: all 0.2s; font-weight: 400; }
  .mino-nav-link:hover { color: rgba(255,255,255,0.72); background: rgba(255,255,255,0.06); }

  .post-card { transition: border-color 0.3s, background 0.3s, transform 0.3s; }
  .post-card:hover { border-color: rgba(255,255,255,0.16) !important; background: rgba(255,255,255,0.05) !important; transform: translateY(-3px); }
  .post-card:hover .post-arrow { transform: translateX(5px); }
  .post-arrow { transition: transform 0.25s; }
`;

const Background = () => (
  <>
    <div className="mino-aurora" />
    <div className="mino-dot-grid" />
    <div className="mino-beam" style={{ '--from': '8%',  '--to': '12%', '--dur': '30s' }} />
    <div className="mino-beam" style={{ '--from': '38%', '--to': '42%', '--dur': '38s', opacity: 0.6 }} />
    <div className="mino-beam" style={{ '--from': '68%', '--to': '72%', '--dur': '25s', opacity: 0.4 }} />
  </>
);

const Nav = ({ navigate, right }) => (
  <nav style={{ position: 'sticky', top: 0, zIndex: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
    <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
      <svg width="26" height="26" viewBox="0 0 38 38" fill="none">
        <rect x="1" y="1" width="36" height="36" rx="9" fill="#050505" stroke="#1a1a1e" strokeWidth="1.5"/>
        <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
      <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: '-0.3px', color: '#e8e6e0' }}>Mino</span>
    </div>
    {right}
  </nav>
);

const Footer = ({ navigate }) => (
  <div style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)', padding: '24px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', fontWeight: 300 }}>© 2026 Mino · DAFCOM ENTERPRISES</span>
    <span onClick={() => navigate('/markets')} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', cursor: 'pointer', fontWeight: 300, transition: 'color 0.2s' }}
      onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.48)'}
      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.16)'}>
      Back to markets
    </span>
  </div>
);

const POSTS = [
  {
    slug: 'why-we-built-mino',
    title: 'Why we built Mino',
    subtitle: 'A prediction markets platform was never the obvious thing to build in Nairobi. Here is why we did it anyway.',
    date: 'May 2026',
    author: 'Martin W. David',
    readTime: '5 min read',
    category: 'Founder',
    content: [
      { type: 'p', text: `There is a particular feeling you get when you watch a major event unfold — an election, a World Cup final, a budget announcement — and you think to yourself: I knew that was going to happen. Not just a guess. An informed conviction built on research, pattern recognition, and a real understanding of what was at stake.` },
      { type: 'p', text: `That feeling has economic value. In most of the world, you can act on it. In Kenya, you could not. Not properly, not transparently, not with a platform built for it. That is why we built Mino.` },
      { type: 'h2', text: 'Prediction markets are not gambling' },
      { type: 'p', text: `This is the first thing people get wrong. A prediction market is a mechanism for aggregating collective intelligence about the future. When thousands of people allocate real money to outcomes they believe in, the resulting probability reflects the best available estimate of what will happen. This is why prediction markets routinely outperform polls, analysts, and expert forecasters.` },
      { type: 'p', text: `Betfair, Polymarket, Kalshi — these platforms exist in the US and Europe. They are used by researchers, traders, journalists, and policymakers. They move information efficiently. They have real utility. And until Mino, nothing like them existed in Kenya or East Africa.` },
      { type: 'h2', text: 'The M-PESA unlock' },
      { type: 'p', text: `Building a prediction markets platform in Kenya requires solving one hard problem above all others: payments. Kenya has no Stripe. Credit card penetration is low. But M-PESA has over 90% adult penetration. Every Kenyan with a phone can send and receive money instantly.` },
      { type: 'p', text: `Safaricom's Daraja API gives developers programmatic access to M-PESA. That is the unlock. The payment infrastructure that makes Mino possible has been sitting there for years. We just built on top of it.` },
      { type: 'h2', text: 'What we built' },
      { type: 'p', text: `Mino is a full-stack prediction markets platform. Binary markets use LMSR pricing — the same mechanism used by academic and institutional prediction market platforms globally. Multi-choice markets use parimutuel pooling. Fees are transparent. Withholding tax on net gains is applied automatically.` },
      { type: 'p', text: `The platform was built entirely by one developer — me — over the course of roughly two years. Every line of code, every API integration, every design decision was made in Kenya.` },
      { type: 'h2', text: 'Why now' },
      { type: 'p', text: `Kenya's 2027 general elections are approaching. Political engagement on X is as intense as anywhere in the world. Sports culture is deeply embedded. What was missing was the infrastructure. Mino is that infrastructure.` },
      { type: 'p', text: `We are early, we are small, and we are operating in a regulatory grey area that we intend to navigate responsibly. But we are live, we are processing real transactions, and the markets are open.` },
      { type: 'h2', text: 'What comes next' },
      { type: 'p', text: `We are focused on three things: growing the user base, deepening the market catalogue, and pursuing regulatory clarity. We are also open to investors who understand what we are building and why it matters.` },
      { type: 'p', text: `If you have been waiting for a prediction markets platform built for Kenya — this is it. The markets are open. Come make your call.` },
    ],
  },
];

function BlogPost({ navigate }) {
  const { slug } = useParams();
  const post = POSTS.find(p => p.slug === slug);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setHeroVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  if (!post) return (
    <div style={{ minHeight: '100vh', background: '#000000', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Outfit', sans-serif" }}>
      <div style={{ textAlign: 'center' }}>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>Post not found.</p>
        <button onClick={() => navigate('/blog')} style={{ background: '#e8e6e0', color: '#000', border: 'none', borderRadius: 8, padding: '10px 20px', cursor: 'pointer', fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500 }}>Back to blog</button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#000000', fontFamily: "'Outfit', sans-serif", color: '#e8e6e0', position: 'relative', overflowX: 'hidden' }}>
      <Helmet>
        <title>{post.title} — Mino Blog</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <meta name="description" content={post.subtitle} />
      </Helmet>
      <style>{SHARED_STYLES}</style>
      <Background />
      <Nav navigate={navigate} right={
        <span className="mino-nav-link" onClick={() => navigate('/blog')}>All posts</span>
      } />

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '0 48px', position: 'relative', zIndex: 1 }}>
        <div style={{ paddingTop: 80, paddingBottom: 56, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(32px)', transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.9s cubic-bezier(0.16,1,0.3,1)' }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 32 }}>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', background: 'rgba(255,255,255,0.06)', padding: '4px 10px', borderRadius: 5 }}>{post.category}</span>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.2)', fontWeight: 300 }}>{post.date}</span>
            <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.2)', fontWeight: 300 }}>{post.readTime}</span>
          </div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(32px,5vw,52px)', fontWeight: 700, lineHeight: 1.08, letterSpacing: '-1.8px', color: '#e8e6e0', marginBottom: 20 }}>
            {post.title}
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, color: 'rgba(255,255,255,0.32)', lineHeight: 1.65, fontStyle: 'italic', fontWeight: 300, marginBottom: 36 }}>
            {post.subtitle}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 34, height: 34, borderRadius: 9, background: '#e8e6e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 14, fontWeight: 700, color: '#000' }}>M</span>
            </div>
            <div>
              <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 13, fontWeight: 600, color: '#e8e6e0' }}>{post.author}</p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.26)', fontWeight: 300 }}>Founder & CEO, Mino Markets</p>
            </div>
          </div>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 60 }} />

        <div style={{ paddingBottom: 100 }}>
          {post.content.map((block, i) => {
            if (block.type === 'h2') return (
              <FadeUp key={i} style={{ marginBottom: 18, marginTop: 52 }}>
                <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, letterSpacing: '-0.6px', color: '#e8e6e0', lineHeight: 1.22 }}>{block.text}</h2>
              </FadeUp>
            );
            return (
              <FadeUp key={i} style={{ marginBottom: 24 }}>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, color: 'rgba(255,255,255,0.46)', lineHeight: 1.9, letterSpacing: '-0.1px', fontWeight: 300 }}>{block.text}</p>
              </FadeUp>
            );
          })}
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 52 }} />

        <FadeUp style={{ marginBottom: 100 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
            <div>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.24)', marginBottom: 4, fontWeight: 300 }}>Ready to try it?</p>
              <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, color: '#e8e6e0' }}>The markets are open.</p>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => navigate('/markets')} style={{ background: '#e8e6e0', color: '#000', border: 'none', borderRadius: 9, padding: '11px 22px', fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'opacity 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                Browse markets
              </button>
              <button onClick={() => navigate('/blog')} style={{ background: 'transparent', color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9, padding: '11px 22px', fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 400, cursor: 'pointer', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.22)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.3)'; }}>
                All posts
              </button>
            </div>
          </div>
        </FadeUp>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
}

function BlogIndex({ navigate }) {
  const [heroVisible, setHeroVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setHeroVisible(true), 80); return () => clearTimeout(t); }, []);

  return (
    <div style={{ minHeight: '100vh', background: '#000000', fontFamily: "'Outfit', sans-serif", color: '#e8e6e0', position: 'relative', overflowX: 'hidden' }}>
      <Helmet>
        <title>Blog — Mino Markets</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <meta name="description" content="Thoughts on prediction markets, Kenya, and building Mino." />
      </Helmet>
      <style>{SHARED_STYLES}</style>
      <Background />
      <Nav navigate={navigate} right={
        <div style={{ display: 'flex', gap: 2 }}>
          {[['Markets', '/markets'], ['About', '/about'], ['Investors', '/investors']].map(([l, p]) => (
            <span key={p} className="mino-nav-link" onClick={() => navigate(p)}>{l}</span>
          ))}
        </div>
      } />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 48px', position: 'relative', zIndex: 1 }}>
        <div style={{ paddingTop: 100, paddingBottom: 72, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(32px)', transition: 'opacity 0.9s cubic-bezier(0.16,1,0.3,1), transform 0.9s cubic-bezier(0.16,1,0.3,1)' }}>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 24 }}>The Mino Blog</p>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(40px,5.5vw,64px)', fontWeight: 700, lineHeight: 1.06, letterSpacing: '-2.5px', color: '#e8e6e0', marginBottom: 18 }}>
            Ideas, updates,<br /><span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.32)' }}>and how we think.</span>
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 16, color: 'rgba(255,255,255,0.28)', lineHeight: 1.7, fontWeight: 300 }}>
            Thoughts on prediction markets, building in Kenya, and the future of African fintech.
          </p>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginBottom: 52 }} />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingBottom: 100 }}>
          {POSTS.map((post, i) => (
            <div key={post.slug} className="post-card"
              onClick={() => navigate(`/blog/${post.slug}`)}
              style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: '36px 38px', cursor: 'pointer', background: 'rgba(255,255,255,0.025)', opacity: 0, animation: `fadeSlideIn 0.7s cubic-bezier(0.16,1,0.3,1) ${0.1 * i + 0.1}s forwards` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.28)', background: 'rgba(255,255,255,0.06)', padding: '3px 9px', borderRadius: 4 }}>{post.category}</span>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.2)', fontWeight: 300 }}>{post.date}</span>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.2)', fontWeight: 300 }}>{post.readTime}</span>
                  </div>
                  <h2 style={{ fontFamily: "'Syne', sans-serif", fontSize: 26, fontWeight: 700, letterSpacing: '-0.6px', color: '#e8e6e0', marginBottom: 10, lineHeight: 1.22 }}>{post.title}</h2>
                  <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, color: 'rgba(255,255,255,0.28)', lineHeight: 1.7, marginBottom: 22, fontWeight: 300 }}>{post.subtitle}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: '#e8e6e0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 10, fontWeight: 700, color: '#000' }}>M</span>
                    </div>
                    <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.28)', fontWeight: 400 }}>{post.author}</span>
                  </div>
                </div>
                <div className="post-arrow" style={{ flexShrink: 0, marginTop: 6 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
              </div>
            </div>
          ))}

          <div style={{ border: '1px solid rgba(255,255,255,0.05)', borderRadius: 18, padding: '32px 38px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.01)' }}>
            <div>
              <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.2)', marginBottom: 4 }}>More posts coming soon</p>
              <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.12)', fontWeight: 300 }}>Market updates, product news, and deep dives on African fintech.</p>
            </div>
          </div>
        </div>
      </div>
      <Footer navigate={navigate} />
    </div>
  );
}

export default function Blog() {
  const navigate = useNavigate();
  return (
    <Routes>
      <Route index element={<BlogIndex navigate={navigate} />} />
      <Route path=":slug" element={<BlogPost navigate={navigate} />} />
    </Routes>
  );
}